import React, { useState } from 'react';
import {
  ArchiveIcon,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  EditIcon,
  MyLocationIcon,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@billda/design-system';
import { PermissionGate } from '../../../../../shared/components/gates/PermissionGate';
import { Permissions } from '@billda/permissions-ui';
import { usePermission } from '@billda/permissions-ui';
import { SiteCardHeader } from '../molecules/SiteCardHeader';
import { SiteMetricsDisplay } from '../molecules/SiteMetricsDisplay';
import { FinancialSummaryRow } from '../molecules/FinancialSummaryRow';
import { ProgressBar } from '../atoms/ProgressBar';
import { EditSiteDialog } from './EditSiteDialog';
import { ArchiveSiteConfirmDialog } from './ArchiveSiteConfirmDialog';

export interface SiteDetailsHeaderProps {
  site: {
    id: number;
    name: string;
    slug: string;
    what3WordsAddress: string | null;
    notes?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    archivedAt?: string | null;
  };
  financialMetrics?: {
    totalLabourPrice: number;
    approvedAmount: number;
    bookedAmount: number;
    remaining: number;
    progress: number;
  } | null;
  plotCount?: number;
  isLoading?: boolean;
  onBackfillCoordinates?: () => void;
  isBackfilling?: boolean;
}

export const SiteDetailsHeader: React.FC<SiteDetailsHeaderProps> = ({
  site,
  financialMetrics,
  plotCount = 0,
  isLoading = false,
  onBackfillCoordinates,
  isBackfilling = false,
}) => {
  const hasImportPermission = usePermission(Permissions.Sites.Import);
  const hasEditPermission = usePermission(Permissions.Sites.Edit);
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const isArchived = site.archivedAt != null;

  // Check if backfill should show: coordinates missing + has import permission + handler provided
  const showBackfill =
    site.latitude == null &&
    hasImportPermission &&
    Boolean(onBackfillCoordinates);

  return (
    <Card data-testid="site-header-strip" className="w-full">
      <EditSiteDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        site={{
          id: site.id,
          name: site.name,
          what3WordsAddress: site.what3WordsAddress,
          notes: site.notes ?? null,
          latitude: site.latitude,
          longitude: site.longitude,
        }}
      />
      <ArchiveSiteConfirmDialog
        open={archiveOpen}
        onClose={() => setArchiveOpen(false)}
        siteId={site.id}
      />
      <CardContent className="py-3">
        <Stack spacing={1}>
          {/* Site name and What3Words - always visible */}
          {isLoading ? (
            <div>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="40%" height={24} />
            </div>
          ) : (
            <SiteCardHeader
              name={site.name}
              slug={site.slug}
              what3WordsAddress={site.what3WordsAddress || undefined}
              latitude={site.latitude}
              longitude={site.longitude}
              compactDirections
              actions={
                isArchived ? (
                  <Chip
                    label="Archived"
                    size="sm"
                    data-testid="site-archived-header-chip"
                  />
                ) : hasEditPermission ? (
                  <>
                    <Tooltip content="Edit site">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit site"
                        data-testid="site-edit-button"
                        onClick={() => setEditOpen(true)}
                        className="size-8"
                      >
                        <EditIcon aria-hidden="true" className="size-4" />
                      </Button>
                    </Tooltip>
                    <Tooltip content="Archive site">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Archive site"
                        data-testid="site-archive-button"
                        onClick={() => setArchiveOpen(true)}
                        className="size-8"
                      >
                        <ArchiveIcon aria-hidden="true" className="size-4" />
                      </Button>
                    </Tooltip>
                  </>
                ) : null
              }
            />
          )}

          {showBackfill && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onBackfillCoordinates}
              disabled={isBackfilling}
              loading={isBackfilling}
              data-testid="backfill-coordinates-button"
              className="self-start"
            >
              {isBackfilling ? null : (
                <MyLocationIcon className="size-4" aria-hidden="true" />
              )}
              Backfill Coordinates
            </Button>
          )}

          <Divider />

          {/* Financial metrics - permission-gated */}
          <PermissionGate permission="sites.financial.read">
            {financialMetrics ? (
              <Stack spacing={1}>
                {/* Total Value and Plot Count */}
                {isLoading ? (
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="80%" height={32} />
                    </div>
                    <div className="flex-1">
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="80%" height={32} />
                    </div>
                  </div>
                ) : (
                  <SiteMetricsDisplay
                    totalLabourPrice={financialMetrics.totalLabourPrice}
                    plotCount={plotCount}
                  />
                )}

                {/* Progress Bar */}
                {isLoading ? (
                  <div>
                    <Skeleton variant="text" width="30%" height={20} />
                    <Skeleton variant="rectangular" width="100%" height={8} />
                  </div>
                ) : (
                  <div>
                    <Text
                      variant="caption"
                      color="secondary"
                      className="mb-1 block"
                    >
                      Progress
                    </Text>
                    <ProgressBar
                      percentage={financialMetrics.progress}
                      variant="full-width"
                      size="large"
                    />
                  </div>
                )}

                {/* Financial Summary Row */}
                {isLoading ? (
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="80%" height={32} />
                    </div>
                    <div className="flex-1">
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="80%" height={32} />
                    </div>
                    <div className="flex-1">
                      <Skeleton variant="text" width="50%" height={20} />
                      <Skeleton variant="text" width="80%" height={32} />
                    </div>
                  </div>
                ) : (
                  <FinancialSummaryRow
                    approved={financialMetrics.approvedAmount}
                    booked={financialMetrics.bookedAmount}
                    remaining={financialMetrics.remaining}
                  />
                )}
              </Stack>
            ) : null}
          </PermissionGate>
        </Stack>
      </CardContent>
    </Card>
  );
};
